///
/// EarthBrowser
///
/// Created by Matt Giger on 12/2/2025
/// Copyright (c) 2025 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

/**
 * Hover event data - emitted when mouse hovers over the canvas
 */
export interface HoverGestureEventData extends GestureEventData {
	/** Movement delta since last event */
	deltaX: number
	deltaY: number
}

export interface HoverGestureConfig extends GestureRecognizerConfig {
	/** Minimum movement in pixels before emitting a change event (default: 1) */
	minimumMovement?: number
	/** Throttle hover events to this interval in ms (default: 0 = no throttle) */
	throttleMs?: number
}

/**
 * HoverGestureRecognizer - Detects hover (mouse-over) gestures
 *
 * Extends ContinuousGestureRecognizer for a unified interface with other recognizers.
 * Hit testing and routing to specific nodes is handled by GestureSystem.
 *
 * Unlike touch gesture recognizers:
 * - Only works with mouse/pointer events (not touch)
 * - Tracks movement when NO buttons are pressed
 * - Doesn't participate in the gesture arena
 *
 * States:
 * - Began: Mouse entered the canvas / started hovering over node
 * - Changed: Mouse moved within the canvas
 * - Ended: Mouse left the canvas / stopped hovering over node
 *
 * Event Flow:
 * 1. GestureSystem calls handlePointerEnter/Move/Leave
 * 2. This recognizer emits hover data with position
 * 3. Callbacks receive the HoverGestureEventData
 */
export class HoverGestureRecognizer extends ContinuousGestureRecognizer<HoverGestureEventData> {
	private minimumMovement: number
	private throttleMs: number

	// Current hover state
	private isHovering: boolean = false
	private currentX: number = 0
	private currentY: number = 0
	private lastX: number = 0
	private lastY: number = 0
	private lastEventTime: number = 0

	constructor(config: HoverGestureConfig = {}) {
		super({
			...config,
			// Hover doesn't use touch counts, but set reasonable defaults
			minimumTouches: 0,
			maximumTouches: 0
		})
		this.minimumMovement = config.minimumMovement ?? 1
		this.throttleMs = config.throttleMs ?? 0
		// Hover is always simultaneous - it doesn't compete in the arena
		this._allowSimultaneous = true
	}

	get gestureType(): string {
		return "hover"
	}

	/**
	 * Get current hover position
	 */
	getPosition(): { x: number; y: number } {
		return { x: this.currentX, y: this.currentY }
	}

	/**
	 * Check if currently hovering
	 */
	override isActive(): boolean {
		return this.isHovering
	}

	// ========== Pointer Event Handlers (called by GestureSystem) ==========

	/**
	 * Handle pointer entering the hover area
	 * Hover events bypass the arena (no competition), so we auto-win.
	 */
	handlePointerEnter(touch: TouchPoint): void {
		if (!this._enabled) return

		this.isHovering = true
		this.currentX = touch.x
		this.currentY = touch.y
		this.lastX = touch.startX
		this.lastY = touch.startY
		this.lastEventTime = performance.now()

		const data = this.createEventData(GestureState.Began)
		this.begin(data)
		// Hover bypasses arena, so auto-win to emit the pending Began event
		this.didBecomeActive()
	}

	/**
	 * Handle pointer moving (when no buttons pressed)
	 */
	handlePointerMove(touch: TouchPoint): void {
		if (!this._enabled || !this.isHovering) return

		const now = performance.now()

		// Check throttle
		if (this.throttleMs > 0 && (now - this.lastEventTime) < this.throttleMs) {
			return
		}

		// Check minimum movement
		const dx = touch.x - this.lastX
		const dy = touch.y - this.lastY
		const distance = Math.sqrt(dx * dx + dy * dy)

		if (distance < this.minimumMovement) {
			return
		}

		// Store previous position
		this.lastX = this.currentX
		this.lastY = this.currentY
		this.currentX = touch.x
		this.currentY = touch.y
		this.lastEventTime = now

		const data = this.createEventData(GestureState.Changed)
		data.ownerHits = touch.gestureHits.map(h => ({
			hitPoint: h.hitPoint,
			hitDistance: h.distance
		}))
		this.change(data)
	}

	/**
	 * Handle pointer leaving the hover area
	 */
	handlePointerLeave(): void {
		if (!this.isHovering) return

		this.isHovering = false

		const data = this.createEventData(GestureState.Ended)
		this.end(data)
	}

	// ========== Touch Event Handlers (required by base class) ==========
	// These convert touch events to pointer events for hover consistency

	handleTouchStart(_touches: TouchPoint[]): void {
		// Hover doesn't respond to touch - touch gestures use other recognizers
	}

	handleTouchMove(_touches: TouchPoint[]): void {
		// Hover doesn't respond to touch - touch gestures use other recognizers
	}

	handleTouchEnd(_touches: TouchPoint[]): void {
		// Hover doesn't respond to touch - touch gestures use other recognizers
	}

	handleTouchCancel(): void {
		if (this.isHovering) {
			this.isHovering = false
			this.cancel()
		}
	}

	// ========== Event Data Creation ==========

	protected createEventData(state: GestureState): HoverGestureEventData {
		return {
			type: "hover",
			state,
			timestamp: performance.now(),
			x: this.currentX,
			y: this.currentY,
			ownerHits: this.getOwnerHits(),
			deltaX: this.currentX - this.lastX,
			deltaY: this.currentY - this.lastY
		}
	}

	override reset(): void {
		super.reset()
		this.isHovering = false
		this.currentX = 0
		this.currentY = 0
		this.lastX = 0
		this.lastY = 0
		this.lastEventTime = 0
	}

	override cancel(): void {
		if (this.isHovering) {
			this.isHovering = false
		}
		super.cancel()
	}
}
